// Import React
import { useEffect, useState } from 'react';

// Import Icons
import { CiEdit } from "react-icons/ci";
import { FaCircle } from "react-icons/fa";
import { FaNetworkWired } from "react-icons/fa6";
import { IoSettingsOutline } from "react-icons/io5";
import { MdPermMedia, MdDeleteOutline } from "react-icons/md";
import { IoLogoGameControllerB, IoMdMore, IoMdAdd } from "react-icons/io";

// Import Custom UI
import { DefaultText } from '../functions/TextModule';

// Import Custom Functions
import { appConfig, appFunc } from '../functions/FuncHeader';
import { clearLocalStorage } from '../functions/LoginCheck';

/* =====================================
   D E F I N I T I O N
   ===================================== */

const dataMode = appFunc.appEnum.createEnum([
    "invalid",
    "addGroup",
    "addContent",
    "addExtra",
    "editGroup",
    "editContent",
    "editExtra",
    "removeGroup",
    "removeContent",
    "removeExtra",
    "lastEnum"
]);

const homePropList = appFunc.appEnum.createEnum([
    "hpLoadingState",
    "isEditMode",
    "getDataMode",
    "getDataList",
    "getDataListMoreState",
    "getOnlineStatus",
    "modifyData1Value",
    "modifyData2Value",
    "modifyData3Value",
]);

const onlineStatusMode = appFunc.appEnum.createEnum([
    "offline",
    "online",
    "unhealthy",    
]);

const HomeHeader = "HomePage";

/* =====================================
   F U N C T I O N S
   ==================================== */

export default function Home({getAppConfig, setAppConfig = () => {}})
{
    // Initialise
    const [getHomeProp, setHomeProp] = useState({
        [homePropList.hpLoadingState]:          {},
        [homePropList.isEditMode]:              false,
        [homePropList.getDataMode]:             {"type": dataMode.invalid, "dataID": -1},
        [homePropList.getDataList]:             [],
        [homePropList.getDataListMoreState]:    {},
        [homePropList.getOnlineState]:          {},
        [homePropList.modifyData1Value]:        "",
        [homePropList.modifyData2Value]:        "",
        [homePropList.modifyData3Value]:        "",
    });

    /* Function: useEffect
       Info: To run on page load once
       Returns: void
    */
    useEffect(() => {
        // Scroll to the top on load
        window.scrollTo(0, 0);
        
    }, [])

    /* Function: useEffect
       Info: retrieve dashboard content from API 
             if there is a change in login status and user is logged in
       Returns: void
    */
    useEffect(() => {
        if (getAppConfig[appConfig.isUserLogin] === true)
        {
            getDashboardContent();
        }
        else
        {
            // DO NOTHING, SHOW EMPTY PAGE
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAppConfig[appConfig.isUserLogin]])

    /* Function: useEffect
       Info: Update loading state for loading UI to be displayed.
             loading state is updated false if all keys in hpLoadingState is false
       Returns: void
    */
    useEffect(() => {
        let isLoading = false;
        for (let state in getHomeProp[homePropList.hpLoadingState])
        {
            if (getHomeProp[homePropList.hpLoadingState][state] === true) 
            {
                isLoading = state;
                break;
            }
            else
            {
                // Continue Searching
            }
        }
        appFunc.updateUseState.object(appConfig.isLoadingScreen, isLoading, setAppConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getHomeProp[homePropList.hpLoadingState]])

    /* Function: clearModifyDataFields()
       Info: Clear all text fields for data modification pop up
             and reset dataMode trigger regardless
       Returns: void
    */
    function clearModifyDataFields()
    {
        appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.invalid, "dataID": -1}, setHomeProp);
        appFunc.updateUseState.object(homePropList.modifyData1Value, "", setHomeProp);
        appFunc.updateUseState.object(homePropList.modifyData2Value, "", setHomeProp);
        appFunc.updateUseState.object(homePropList.modifyData3Value, "", setHomeProp);
    }

    /* Function: getDashboardContent() 
       Info: Retrieves dashboard content from API
       Returns: void
    */
    function getDashboardContent() 
    {
        appFunc.updateUseState.object([homePropList.hpLoadingState, "getDashboardContent"], true, setHomeProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        let content_url =  process.env.REACT_APP_API_BASE_URL + "/items/content"; 
        fetch(content_url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            }
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                        "[getDashboardContent] Failed to get data list.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[getDashboardContent] ${response["Error"]}`);
            }
            else
            {
                appFunc.updateUseState.object(homePropList.getDataList, response, setHomeProp);
                response.forEach((group) => {
                    group.GroupContent.forEach((content) => {
                        pingAppStatus(content.ContentID);
                        appFunc.updateUseState.object([homePropList.getOnlineState, content.ContentID], 0, setHomeProp);
                        appFunc.updateUseState.object([homePropList.getDataListMoreState, content.ContentID], false, setHomeProp);
                    })
                });
            }
            appFunc.updateUseState.object([homePropList.hpLoadingState, "getDashboardContent"], false, setHomeProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[getDashboardContent] ${error}`);
            appFunc.updateUseState.object([homePropList.hpLoadingState, "getDashboardContent"], false, setHomeProp);
        });
    }

    /* Function: updateDashboardData()
       Info: update dashboard data according to dataMode - group, content, or extra.
             to add or edit dashboard data
       Returns: void
    */
    function updateDashboardData()
    {
        appFunc.updateUseState.object([homePropList.hpLoadingState, "updateDashboardData"], true, setHomeProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        let content_url =  process.env.REACT_APP_API_BASE_URL;
        if (getHomeProp[homePropList.getDataMode]["type"] === dataMode.addGroup ||
            getHomeProp[homePropList.getDataMode]["type"] === dataMode.addContent ||
            getHomeProp[homePropList.getDataMode]["type"] === dataMode.addExtra)
        {
            content_url += "/items/content/create-item";
        }
        else if (getHomeProp[homePropList.getDataMode]["type"] === dataMode.editGroup ||
            getHomeProp[homePropList.getDataMode]["type"] === dataMode.editContent ||
            getHomeProp[homePropList.getDataMode]["type"] === dataMode.editExtra)
        {
            content_url += "/items/content/update-item";
        }
        else
        {
            appFunc.updateUseState.object([homePropList.hpLoadingState, "updateDashboardData"], false, setHomeProp);
            return;
        }
        let data1 = getHomeProp[homePropList.modifyData1Value];
        let data2 = getHomeProp[homePropList.modifyData2Value];
        let data3 = getHomeProp[homePropList.modifyData3Value];
        let updateDataUid = getHomeProp[homePropList.getDataMode]["dataID"];
        let updateDataType = getHomeProp[homePropList.getDataMode]["type"];
        fetch(content_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify({
                "dataID": parseInt(updateDataUid),
                "dataType": parseInt(updateDataType),
                "data1Value": data1,
                "data2Value": data2,
                "data3Value": data3
            })
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                        "[updateDashboardData] Failed to update data.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[updateDashboardData] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                getDashboardContent();
                clearModifyDataFields();
            }
            else 
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                    "[updateDashboardData] Unknown response.");
            }
            appFunc.updateUseState.object([homePropList.hpLoadingState, "updateDashboardData"], false, setHomeProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[updateDashboardData] ${error}`);
            appFunc.updateUseState.object([homePropList.hpLoadingState, "updateDashboardData"], false, setHomeProp);
        });
    }

    /* Function: deleteDashboardData()
       Info: delete dashboard data according to dataMode - group, content, or extra.
       Returns: void
    */
    function deleteDashboardData()
    {
        appFunc.updateUseState.object([homePropList.hpLoadingState, "deleteDashboardData"], true, setHomeProp);
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        let content_url =  process.env.REACT_APP_API_BASE_URL + "/items/content/delete-item";;
        let updateDataUid = getHomeProp[homePropList.getDataMode]["dataID"];
        let updateDataType = getHomeProp[homePropList.getDataMode]["type"];
        fetch(content_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify({
                "dataID": parseInt(updateDataUid),
                "dataType": parseInt(updateDataType)
            })
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                        "[deleteDashboardData] Failed to update data.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[deleteDashboardData] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                getDashboardContent();
                appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.invalid, "dataID": -1}, setHomeProp);
            }
            else 
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                    "[deleteDashboardData] Unknown response.");
            }
            appFunc.updateUseState.object([homePropList.hpLoadingState, "deleteDashboardData"], false, setHomeProp);
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[deleteDashboardData] ${error}`);
            appFunc.updateUseState.object([homePropList.hpLoadingState, "deleteDashboardData"], false, setHomeProp);
        });
    }

    /* Function: deleteDashboardData()
       Info: Ping for content's availability status from server
       Returns: void
    */
    function pingAppStatus(contentID)
    {
        let token = localStorage.getItem(process.env.REACT_APP_API_ACCESS_TOKEN);
        let content_url =  process.env.REACT_APP_API_BASE_URL + "/items/content/online-status";
        fetch(content_url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify({
                "dataID": parseInt(contentID)
            })
        })
        .then(response => response.json())
        .then(response => {
            if (response["detail"] !== undefined)
            {
                if (response["detail"] === "Could not validate credentials")
                {
                    clearLocalStorage(setAppConfig);
                }
                else
                {
                    appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                        "[pingAppStatus] Failed to ping for data.");
                }
            }
            else if (response["Error"] !== undefined)
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[pingAppStatus] ${response["Error"]}`);
            }
            else if (response["Success"] !== undefined)
            {
                appFunc.updateUseState.object([homePropList.getOnlineState, contentID], response["Success"], setHomeProp);
            }
            else 
            {
                appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, 
                    "[pingAppStatus] Unknown response.");
            }
        })
        .catch(error => {
            appFunc.dbprint(appFunc.severitiesLevel.PRINT_ERROR, HomeHeader, `[pingAppStatus] ${error}`);
        });
    }

    return (
        <div className="HomePage">
            <div className="HomePage-Container">
                {
                    (getHomeProp[homePropList.getDataMode]["type"] !== dataMode.invalid && getHomeProp[homePropList.isEditMode] === true && getAppConfig[appConfig.isUserAdmin] === true) ?
                    (getHomeProp[homePropList.getDataMode]["type"] === dataMode.addGroup || getHomeProp[homePropList.getDataMode]["type"] === dataMode.editGroup) ?
                        <div className="HomePage-AddData-Container">
                            <div className="HomePage-AddData-Container-Inner">
                                <div className="HomePage-AddData-Container-Title">
                                    {`${getHomeProp[homePropList.getDataMode]["type"] === dataMode.addGroup ? "Add" : "Edit"} Group`}
                                </div>
                                <DefaultText
                                    txtLabel={"Name:"}
                                    inputMaxLen={30}
                                    inputID={"modGrpName"}
                                    inputValue={getHomeProp[homePropList.modifyData1Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData1Value, event.target.value, setHomeProp)}
                                />
                                <div className="HomePage-AddData-Button-Container">
                                    <button className="HomePage-AddData-Button save"
                                        onClick={() => {
                                            updateDashboardData();
                                        }}>Save
                                    </button>
                                    <button className="HomePage-AddData-Button cancel" 
                                        onClick={() => {
                                            clearModifyDataFields();
                                            }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div> :
                    (getHomeProp[homePropList.getDataMode]["type"] === dataMode.addContent || getHomeProp[homePropList.getDataMode]["type"] === dataMode.editContent) ?
                        <div className="HomePage-AddData-Container">
                            <div className="HomePage-AddData-Container-Inner">
                                <div className="HomePage-AddData-Container-Title">
                                    {`${getHomeProp[homePropList.getDataMode]["type"] === dataMode.addContent ? "Add" : "Edit"} Content`} 
                                </div>
                                <DefaultText
                                    txtLabel={"Name:"}
                                    inputMaxLen={30}
                                    inputID={"modCtntName"}
                                    inputValue={getHomeProp[homePropList.modifyData1Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData1Value, event.target.value, setHomeProp)}
                                />
                                <DefaultText
                                    txtLabel={"URL:"}
                                    inputMaxLen={100}
                                    inputID={"modCtntUrl"}
                                    inputValue={getHomeProp[homePropList.modifyData2Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData2Value, event.target.value, setHomeProp)}
                                />
                                <DefaultText
                                    txtLabel={"Image Path:"}
                                    inputMaxLen={300}
                                    inputID={"modCtntImgPath"}
                                    inputValue={getHomeProp[homePropList.modifyData3Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData3Value, event.target.value, setHomeProp)}
                                />
                                <div className="HomePage-AddData-Button-Container">
                                    <button className="HomePage-AddData-Button save" 
                                        onClick={() => {
                                            updateDashboardData();
                                        }}>Save
                                    </button>
                                    <button className="HomePage-AddData-Button cancel" 
                                        onClick={() => {
                                            clearModifyDataFields();
                                            }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div> :
                    (getHomeProp[homePropList.getDataMode]["type"] === dataMode.addExtra || getHomeProp[homePropList.getDataMode]["type"] === dataMode.editExtra) ?
                        <div className="HomePage-AddData-Container">
                            <div className="HomePage-AddData-Container-Inner">
                                <div className="HomePage-AddData-Container-Title">
                                    {`${getHomeProp[homePropList.getDataMode]["type"] === dataMode.addExtra ? "Add" : "Edit"} Extra`}
                                </div>
                                <DefaultText
                                    txtLabel={"Name:"}
                                    inputMaxLen={30}
                                    inputID={"modExtraName"}
                                    inputValue={getHomeProp[homePropList.modifyData1Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData1Value, event.target.value, setHomeProp)}
                                />
                                <DefaultText
                                    txtLabel={"Type:"}
                                    inputMaxLen={30}
                                    inputID={"modExtraType"}
                                    inputValue={getHomeProp[homePropList.modifyData2Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData2Value, event.target.value, setHomeProp)}
                                />
                                <DefaultText
                                    txtLabel={"Content:"}
                                    inputMaxLen={150}
                                    inputID={"modExtraCtnt"}
                                    inputValue={getHomeProp[homePropList.modifyData3Value]}
                                    changeTask={event => appFunc.updateUseState.object(homePropList.modifyData3Value, event.target.value, setHomeProp)}
                                />
                                <div className="HomePage-AddData-Button-Container">
                                    <button className="HomePage-AddData-Button save" 
                                        onClick={() => {
                                            updateDashboardData();
                                        }}>Save
                                    </button>
                                    <button className="HomePage-AddData-Button cancel" 
                                        onClick={() => {
                                            clearModifyDataFields();
                                            }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div> :
                    (getHomeProp[homePropList.getDataMode]["type"] === dataMode.removeGroup || 
                    getHomeProp[homePropList.getDataMode]["type"] === dataMode.removeContent || 
                    getHomeProp[homePropList.getDataMode]["type"] === dataMode.removeExtra) ?
                        <div className="DeletePrompt-MainContainer">
                            <div className="DeletePrompt-InnerContainer">
                                <p className="DeletePrompt-Text">
                                    {`Are you sure you want to remove 
                                    ${getHomeProp[homePropList.getDataMode]["type"] === dataMode.removeGroup ? "group": 
                                    getHomeProp[homePropList.getDataMode]["type"] === dataMode.removeContent ? "content": 
                                    getHomeProp[homePropList.getDataMode]["type"] === dataMode.removeExtra ? "content-extra": 
                                    ""} ?`}
                                </p>
                                <div className="DeletePrompt-ButtonContainer">
                                    <button className="DeletePrompt-Button accept" onClick={() => {
                                        deleteDashboardData();
                                    }}>Yes</button>
                                    <button className="DeletePrompt-Button decline" onClick={() => {
                                        appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.invalid, "dataID": -1}, setHomeProp);
                                    }}>No</button>
                                </div>
                            </div>
                        </div>
                    : "" : ""
                }
                <div className="HomePage-Edit-Container">
                    <div className="HomePage-Edit-Button" 
                        onClick={() => {
                            appFunc.updateUseState.object(homePropList.isEditMode, !getHomeProp[homePropList.isEditMode], setHomeProp);
                        }}>
                        <div className="HomePage-Button-Container">
                            <CiEdit />Edit
                        </div>
                    </div>
                    <div className={`HomePage-AddGroup-Button ${getHomeProp[homePropList.isEditMode] ? '': 'hide'}`} 
                        onClick={() => {
                            appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.addGroup, "dataID": -1}, setHomeProp);
                    }}>
                        <div className="HomePage-Button-Container">
                            <IoMdAdd />Add Group
                        </div>
                    </div>
                </div>
                {
                    getHomeProp[homePropList.getDataList] === undefined ? "" :
                    getHomeProp[homePropList.getDataList].map((dashboardData, index) => {
                        return (
                            <div key={index} className="HomePage-GroupContainer">
                                <div className="HomePage-GroupName-Container">
                                    <div className="HomePage-GroupName">
                                        {dashboardData.GroupName.toUpperCase().includes("GAME") ? <IoLogoGameControllerB /> :
                                            dashboardData.GroupName.toUpperCase().includes("MEDIA") ? <MdPermMedia /> :
                                                dashboardData.GroupName.toUpperCase().includes("SYS") ? <IoSettingsOutline /> :
                                                    dashboardData.GroupName.toUpperCase().includes("NETWORK") ? <FaNetworkWired /> :
                                                        ""}
                                        {dashboardData.GroupName}
                                    </div>
                                    <div className={`HomePage-GroupButton-Container ${getHomeProp[homePropList.isEditMode] ? "" : "hide"}`}>
                                        <button className="HomePage-AddData-GroupButton" 
                                            onClick={() => {
                                                appFunc.updateUseState.object(homePropList.modifyData1Value, dashboardData.GroupName, setHomeProp);
                                                appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.editGroup, "dataID": dashboardData.GroupID}, setHomeProp);
                                            }}>
                                            <CiEdit />
                                        </button>
                                        <button className="HomePage-AddData-GroupButton"
                                            onClick={() => {
                                                appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.addContent, "dataID": dashboardData.GroupID}, setHomeProp);
                                            }}>
                                            <IoMdAdd />
                                        </button>
                                        <button className="HomePage-AddData-GroupButton delete"
                                            onClick={() => {
                                                appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.removeGroup, "dataID": dashboardData.GroupID}, setHomeProp);
                                            }}>
                                            <MdDeleteOutline />
                                        </button>
                                    </div>
                                </div>
                                <div className="HomePage-SubGroupMainContainer">
                                    {
                                        dashboardData.GroupContent === undefined ? "" :
                                        dashboardData.GroupContent.map((content, index) => {
                                            return (
                                                <div key={index} className="HomePage-SubGroupIndivContainer">
                                                    {
                                                        (content.URL === "" || content.URL === null) ?
                                                        <div className="HomePage-SubGroupTitlePanel" href={content.URL} target="noreferrer">
                                                            {
                                                                content.Image === "" ? "" :
                                                                    <img className="HomePage-SubGroupImage" alt={content.Name || "#"} src={content.Image} />
                                                            }
                                                            <div className="HomePage-SubGroupName">{content.Name}</div>
                                                        </div> :
                                                        <a className="HomePage-SubGroupTitlePanel" href={content.URL} target="noreferrer">
                                                            {
                                                                content.Image === "" ? "" :
                                                                    <img className="HomePage-SubGroupImage" alt={content.Name || "#"} src={content.Image} />
                                                            }
                                                            <div className="HomePage-SubGroupName">{content.Name}</div>
                                                        </a>
                                                    }
                                                    <div className={`HomePage-SubGroup-OnlineStatus ${
                                                        getHomeProp[homePropList.getOnlineState][content.ContentID] === onlineStatusMode.online ? "online" :
                                                        getHomeProp[homePropList.getOnlineState][content.ContentID] === onlineStatusMode.unhealthy ? "unhealthy" : ""}`}><FaCircle /></div>
                                                    {
                                                        content["Extra"] === undefined && getHomeProp[homePropList.isEditMode] === false ? "" :
                                                        <div className={`HomePage-SubGroupMoreContainer ${getHomeProp[homePropList.getDataListMoreState][content.ContentID] ? "expand" : ""}`}
                                                            onClick={() => {
                                                                appFunc.updateUseState.singleSelectionToggleObject(homePropList.getDataListMoreState, content.ContentID, setHomeProp)
                                                        }}>
                                                            <div className="HomePage-SubGroupMoreIco" >
                                                                <IoMdMore />
                                                            </div>
                                                            {
                                                                getHomeProp[homePropList.getDataListMoreState][content.ContentID] ?
                                                                    <div className="HomePage-SubGroupExtraMenu-Container">
                                                                        {
                                                                            getHomeProp[homePropList.isEditMode] === false && getAppConfig[appConfig.isUserAdmin] === false ? "" :
                                                                            <div className="HomePage-SubGroupExtraMenu-EditMode-Container">
                                                                                <div className="HomePage-SubGroupExtra-Content"
                                                                                    onClick={() => {
                                                                                        appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.removeContent, "dataID": content.ContentID}, setHomeProp);
                                                                                    }}>Remove
                                                                                </div>
                                                                                <div className="HomePage-SubGroupExtra-Content" 
                                                                                    onClick={() => {
                                                                                        appFunc.updateUseState.object(homePropList.modifyData1Value, content.Name, setHomeProp);
                                                                                        appFunc.updateUseState.object(homePropList.modifyData2Value, content.URL, setHomeProp);
                                                                                        appFunc.updateUseState.object(homePropList.modifyData3Value, content.Image, setHomeProp);
                                                                                        appFunc.updateUseState.object(homePropList.getDataMode, {"type": dataMode.editContent, "dataID": content.ContentID}, setHomeProp);
                                                                                    }}>Edit
                                                                                </div>
                                                                                {/* <div className="HomePage-SubGroupExtra-Content" onClick={() => setAddDataMode(AddDataMode.AddExtra)}>Add Extra</div> */}
                                                                                {/* <div className="HomePage-SubGroupExtra-Content" onClick={() => setAddDataMode(AddDataMode.EditExtra)}>Edit Extra</div> */}
                                                                            </div>
                                                                        }
                                                                        {
                                                                            content.Extra === undefined ? "" :
                                                                            content.Extra.map((extra, index) => {
                                                                                return (
                                                                                    <div key={index} className="HomePage-SubGroupMoreName" onClick={() => {
                                                                                        if (extra.Type === "copy")
                                                                                            navigator.clipboard.writeText(extra.Content)
                                                                                        else if (extra.Type === "redirect")
                                                                                            window.open(extra.Content, '_blank', 'noreferrer');
                                                                                    }}>{extra.Name}</div>
                                                                                )
                                                                            })
                                                                        }
                                                                    </div>
                                                                : ""
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    );
}